import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Mail, Copy, Check, FileText, Building2, Newspaper, 
  Home, Heart, Leaf, Star, Edit3, Send, Database, RefreshCw
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface EmailTemplate {
  id: string;
  name: string;
  targetType: string;
  icon: React.ReactNode;
  subject: string;
  body: string;
  variables: string[];
}

interface BacklinkRecord {
  id: string;
  website_name: string;
  website_url: string;
  contact_name: string | null;
  contact_email: string | null;
  category: string;
  outreach_status: string;
  outreach_date: string | null;
  follow_up_count: number | null;
}

const emailTemplates: EmailTemplate[] = [
  {
    id: "review-site",
    name: "Product Review Sites",
    targetType: "Review Site",
    icon: <Star className="h-4 w-4" />,
    subject: "Partnership Opportunity: Premium Smart Toilet Collection for {{site_name}} Review",
    body: `Dear {{contact_name}},

I hope this message finds you well. I'm reaching out from Aureum VII Trading, Europe's premier supplier of luxury smart bathroom technology.

I've been following {{site_name}}'s exceptional coverage of bathroom fixtures and home technology, and I believe our award-winning smart toilet collection would be a perfect fit for your readers.

**Why Aureum VII?**
• Premium European engineering with Japanese precision
• Water-saving technology (up to 50% reduction)
• Integrated bidet, heated seat, and air drying
• B2B focus with white-label options

**What We're Offering:**
• Complimentary review unit (LUNA M9 or LUXA M80)
• Exclusive interview with our product team
• High-resolution product imagery and specifications
• Early access to our 2025 product line

I'd love to discuss how we can collaborate. Would you be available for a brief call next week?

Best regards,
{{sender_name}}
{{sender_title}}
Aureum VII Trading
www.aureum7trading.com`,
    variables: ["site_name", "contact_name", "sender_name", "sender_title"]
  },
  {
    id: "architecture-design",
    name: "Architecture & Design Publications",
    targetType: "Architecture/Design",
    icon: <Building2 className="h-4 w-4" />,
    subject: "Guest Article Pitch: The Future of Luxury Bathroom Design in European Hospitality",
    body: `Dear {{contact_name}},

As an avid reader of {{site_name}}, I've been impressed by your thoughtful coverage of contemporary design trends.

I'm {{sender_name}}, representing Aureum VII Trading—a company at the intersection of luxury design and smart bathroom technology. We work with boutique hotels, high-end developers, and interior designers across Europe.

**Article Pitch: "The Smart Bathroom Revolution: How Technology is Redefining Luxury in European Hotels"**

This piece would explore:
• The rise of intelligent bathroom fixtures in 5-star hospitality
• Case studies from our installations in Spain and Germany
• The balance between minimalist aesthetics and advanced functionality
• Sustainability features that align with green building certifications

I can provide:
• Original photography from our latest installations
• Expert quotes from our design consultants
• Data on the hospitality smart bathroom market

Would you be interested in reviewing a draft? I'm confident it would resonate with your design-conscious audience.

Warm regards,
{{sender_name}}
{{sender_title}}
Aureum VII Trading`,
    variables: ["site_name", "contact_name", "sender_name", "sender_title"]
  },
  {
    id: "diy-remodeling",
    name: "DIY & Remodeling Sites",
    targetType: "DIY & Remodeling",
    icon: <Home className="h-4 w-4" />,
    subject: "Expert Installation Guide: Smart Toilet Setup Made Simple",
    body: `Hi {{contact_name}},

I noticed {{site_name}} provides excellent hands-on guides for home improvement enthusiasts. I'd love to contribute!

At Aureum VII Trading, we've compiled our installation expertise into comprehensive guides that could help your readers confidently tackle smart toilet installations.

**Proposed Content:**

1. **"Complete Smart Toilet Installation Guide: What Contractors Won't Tell You"**
   - Plumbing requirements breakdown
   - Electrical considerations
   - Common mistakes to avoid
   - Time and cost estimates

2. **"Smart Toilet vs. Bidet Seat: Which Upgrade Is Right for Your Bathroom?"**
   - Feature comparison chart
   - Installation complexity comparison
   - Long-term value analysis

**What I Can Provide:**
• Step-by-step photography
• Technical diagrams
• Troubleshooting FAQs
• Video walkthrough options

These would be genuine educational content—not promotional pieces. Our goal is to demystify smart bathroom technology for DIY enthusiasts.

Interested in collaborating?

Best,
{{sender_name}}
{{sender_title}}
Aureum VII Trading`,
    variables: ["site_name", "contact_name", "sender_name", "sender_title"]
  },
  {
    id: "health-senior",
    name: "Health & Senior Care Sites",
    targetType: "Medical/Health",
    icon: <Heart className="h-4 w-4" />,
    subject: "Guest Post Opportunity: Bathroom Accessibility & Aging in Place",
    body: `Dear {{contact_name}},

I'm reaching out because {{site_name}} does important work helping families navigate senior care decisions.

At Aureum VII Trading, we specialize in smart bathroom technology that significantly improves quality of life for seniors and individuals with mobility challenges.

**Proposed Article: "How Smart Toilets Support Independent Living: A Guide for Caregivers"**

Key topics:
• Reducing fall risk with automatic features
• Hygiene benefits for limited mobility users
• How heated seats and warm water improve comfort
• Features that preserve dignity and independence

**Our Perspective:**
We're not just selling products—we've witnessed how these fixtures transform lives. I'd love to share real stories from families who've benefited.

**Additional Resources I Can Provide:**
• Expert interview with our accessibility consultant
• Infographic on bathroom safety statistics
• Product comparison focused on accessibility features

This content aligns perfectly with your mission to support aging in place. Would you consider a guest contribution?

Respectfully,
{{sender_name}}
{{sender_title}}
Aureum VII Trading`,
    variables: ["site_name", "contact_name", "sender_name", "sender_title"]
  },
  {
    id: "sustainability",
    name: "Sustainability & Eco Blogs",
    targetType: "Sustainability Blogs",
    icon: <Leaf className="h-4 w-4" />,
    subject: "Data-Backed Article: Water Conservation in Modern Bathroom Design",
    body: `Hello {{contact_name}},

Your work at {{site_name}} on sustainable living really resonates with our company mission at Aureum VII Trading.

I'd like to propose a data-driven article on water conservation in bathrooms—one of the most overlooked areas for household sustainability.

**Article Concept: "The Hidden Water Waster: How Smart Toilets Cut Bathroom Water Use by 50%"**

**Key Data Points:**
• Average toilet uses 1.6-3.5 gallons per flush
• Our smart toilets use 0.8-1.0 gallons
• Annual savings: 5,000-10,000 gallons per household
• Integrated bidet eliminates toilet paper waste

**Unique Angles:**
• Comparison with bidets in water-scarce regions
• Life cycle environmental analysis
• The toilet paper manufacturing footprint
• European water regulations driving adoption

**What Makes This Different:**
This isn't a product pitch—it's genuine environmental advocacy backed by third-party data. We can provide:
• Independent water usage studies
• Carbon footprint calculations
• Expert quotes on sustainable plumbing

Would this fit your editorial calendar?

Sustainably yours,
{{sender_name}}
{{sender_title}}
Aureum VII Trading`,
    variables: ["site_name", "contact_name", "sender_name", "sender_title"]
  },
  {
    id: "tech-consumer",
    name: "Consumer Tech Publications",
    targetType: "Reviews & Consumer Tech",
    icon: <Newspaper className="h-4 w-4" />,
    subject: "Exclusive Review Opportunity: European Luxury Smart Toilet Technology",
    body: `Hi {{contact_name}},

{{site_name}} consistently delivers the most thorough tech reviews, which is exactly why I'm reaching out.

Aureum VII Trading is bringing European-engineered smart toilets to a market dominated by Japanese and Korean brands. Our approach combines German precision, Italian design sensibility, and Japanese functionality.

**What Makes Us Review-Worthy:**

🔧 **Technology**
• Dual-nozzle bidet with 5 pressure settings
• AI-powered user recognition
• Voice control integration (Alexa/Google compatible)
• Mobile app with health tracking features

📊 **The Story**
• European alternative to TOTO and Kohler
• B2B focus expanding to luxury residential
• Growing hospitality sector adoption

**What We're Offering:**
• Full review unit loan (your choice of model)
• Direct access to our engineering team for Q&A
• Behind-the-scenes look at our design process
• Exclusive discount code for your audience

Our LUNA M9 (Special offer €600 per unit) represents our best technology. We'd love to see how it performs in your hands-on testing.

Interested?

Best,
{{sender_name}}
{{sender_title}}
Aureum VII Trading`,
    variables: ["site_name", "contact_name", "sender_name", "sender_title"]
  },
  {
    id: "followup-1",
    name: "First Follow-up",
    targetType: "Follow-up",
    icon: <Mail className="h-4 w-4" />,
    subject: "Re: {{original_subject}} - Quick Follow-up",
    body: `Hi {{contact_name}},

I wanted to follow up on my previous email about a potential collaboration between Aureum VII Trading and {{site_name}}.

I understand you're busy, so I'll keep this brief:

**Quick Reminder:**
• We're offering complimentary review products
• Original content/guest posts available
• Flexible on timing and format

If this isn't the right fit, no worries at all—I appreciate your time regardless.

Would a quick 10-minute call work better to discuss?

Best regards,
{{sender_name}}`,
    variables: ["contact_name", "site_name", "original_subject", "sender_name"]
  },
  {
    id: "followup-2",
    name: "Second Follow-up",
    targetType: "Follow-up",
    icon: <Mail className="h-4 w-4" />,
    subject: "Final Check-in: Aureum VII Collaboration",
    body: `Hi {{contact_name}},

This will be my last follow-up—I don't want to clutter your inbox!

If the timing isn't right for {{site_name}}, I completely understand. Our offer for review products and guest content remains open whenever you might be interested.

Feel free to reach out anytime: {{sender_email}}

Wishing you continued success,
{{sender_name}}
Aureum VII Trading`,
    variables: ["contact_name", "site_name", "sender_email", "sender_name"]
  }
];

interface VariableInputs {
  [key: string]: string;
}

export function BacklinkEmailTemplates() {
  const { user } = useAuth();
  const [activeTemplate, setActiveTemplate] = useState<string>("review-site");
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [backlinks, setBacklinks] = useState<BacklinkRecord[]>([]);
  const [selectedBacklink, setSelectedBacklink] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [variables, setVariables] = useState<VariableInputs>({
    site_name: "",
    contact_name: "",
    sender_name: "Your Name",
    sender_title: "Business Development Manager",
    sender_email: "hello@aureum7trading.com",
    original_subject: ""
  });

  const currentTemplate = emailTemplates.find(t => t.id === activeTemplate);

  // Fetch backlinks from database
  useEffect(() => {
    if (user) {
      fetchBacklinks();
      
      // Set up realtime subscription
      const channel = supabase
        .channel('backlinks-changes')
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'backlinks' },
          () => {
            fetchBacklinks();
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [user]);

  const fetchBacklinks = async () => {
    if (!user) return;
    
    const { data, error } = await supabase
      .from('backlinks')
      .select('id, website_name, website_url, contact_name, contact_email, category, outreach_status, outreach_date, follow_up_count')
      .eq('agent_id', user.id)
      .order('website_name');

    if (error) {
      console.error('Error fetching backlinks:', error);
      return;
    }

    setBacklinks(data || []);
  };

  // Auto-fill variables when backlink is selected
  useEffect(() => {
    if (selectedBacklink) {
      const backlink = backlinks.find(b => b.id === selectedBacklink);
      if (backlink) {
        setVariables(prev => ({
          ...prev,
          site_name: backlink.website_name,
          contact_name: backlink.contact_name || "",
        }));
      }
    }
  }, [selectedBacklink, backlinks]);

  const replaceVariables = (text: string): string => {
    let result = text;
    Object.entries(variables).forEach(([key, value]) => {
      const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
      result = result.replace(regex, value || `[${key.replace(/_/g, ' ').toUpperCase()}]`);
    });
    return result;
  };

  const copyToClipboard = async (text: string, type: 'subject' | 'body') => {
    try {
      await navigator.clipboard.writeText(replaceVariables(text));
      setCopiedId(type);
      toast.success(`${type === 'subject' ? 'Subject' : 'Email body'} copied to clipboard!`);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      toast.error("Failed to copy to clipboard");
    }
  };

  // Log outreach to database
  const logOutreach = async (isFollowUp: boolean = false) => {
    if (!user || !selectedBacklink) {
      toast.error("Please select a backlink target first");
      return;
    }

    setLoading(true);
    const backlink = backlinks.find(b => b.id === selectedBacklink);
    
    try {
      const updateData: any = {
        outreach_status: isFollowUp ? 'follow_up' : 'contacted',
        last_outreach_date: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      if (!isFollowUp) {
        updateData.outreach_date = new Date().toISOString();
      } else {
        updateData.follow_up_count = (backlink?.follow_up_count || 0) + 1;
      }

      const { error } = await supabase
        .from('backlinks')
        .update(updateData)
        .eq('id', selectedBacklink);

      if (error) throw error;

      toast.success(
        isFollowUp 
          ? `Follow-up #${(backlink?.follow_up_count || 0) + 1} logged for ${backlink?.website_name}` 
          : `Initial outreach logged for ${backlink?.website_name}`
      );
      
      // Refresh backlinks
      fetchBacklinks();
    } catch (error: any) {
      console.error('Error logging outreach:', error);
      toast.error("Failed to log outreach");
    } finally {
      setLoading(false);
    }
  };

  const handleSendAndLog = async () => {
    const isFollowUp = currentTemplate?.targetType === "Follow-up";
    
    // Open email client
    const subject = encodeURIComponent(replaceVariables(currentTemplate?.subject || ""));
    const body = encodeURIComponent(replaceVariables(currentTemplate?.body || ""));
    const backlink = backlinks.find(b => b.id === selectedBacklink);
    const email = backlink?.contact_email || "";
    
    window.open(`mailto:${email}?subject=${subject}&body=${body}`);
    
    // Log the outreach
    await logOutreach(isFollowUp);
  };

  const templatesByType = emailTemplates.reduce((acc, template) => {
    const type = template.targetType;
    if (!acc[type]) acc[type] = [];
    acc[type].push(template);
    return acc;
  }, {} as Record<string, EmailTemplate[]>);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'contacted':
        return <Badge variant="secondary" className="bg-blue-100 text-blue-700">Contacted</Badge>;
      case 'follow_up':
        return <Badge variant="secondary" className="bg-yellow-100 text-yellow-700">Follow-up</Badge>;
      case 'connected':
        return <Badge variant="secondary" className="bg-green-100 text-green-700">Connected</Badge>;
      case 'declined':
        return <Badge variant="secondary" className="bg-red-100 text-red-700">Declined</Badge>;
      default:
        return <Badge variant="outline">Not Contacted</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Backlink Outreach Email Templates
          </CardTitle>
          <CardDescription>
            Personalized email templates connected to your backlink database. Select a target and send emails with automatic tracking.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Backlink Selector */}
          <div className="mb-6 p-4 bg-muted/50 rounded-lg border">
            <div className="flex items-center gap-2 mb-3">
              <Database className="h-4 w-4 text-primary" />
              <Label className="font-medium">Select Backlink Target (Auto-fills variables & logs outreach)</Label>
            </div>
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <Select value={selectedBacklink} onValueChange={setSelectedBacklink}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a backlink target..." />
                  </SelectTrigger>
                  <SelectContent>
                    {backlinks.map(backlink => (
                      <SelectItem key={backlink.id} value={backlink.id}>
                        <div className="flex items-center gap-2">
                          <span>{backlink.website_name}</span>
                          {getStatusBadge(backlink.outreach_status)}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" size="icon" onClick={fetchBacklinks} title="Refresh backlinks">
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
            {selectedBacklink && (
              <div className="mt-3 text-sm text-muted-foreground">
                {(() => {
                  const bl = backlinks.find(b => b.id === selectedBacklink);
                  return bl ? (
                    <div className="flex flex-wrap gap-x-4 gap-y-1">
                      <span>URL: <a href={bl.website_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{bl.website_url}</a></span>
                      {bl.contact_email && <span>Email: {bl.contact_email}</span>}
                      {bl.outreach_date && <span>First Contact: {new Date(bl.outreach_date).toLocaleDateString()}</span>}
                      {bl.follow_up_count && bl.follow_up_count > 0 && <span>Follow-ups: {bl.follow_up_count}</span>}
                    </div>
                  ) : null;
                })()}
              </div>
            )}
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Template Selector */}
            <div className="lg:col-span-1 space-y-4">
              <Label>Select Template</Label>
              <div className="space-y-2">
                {Object.entries(templatesByType).map(([type, templates]) => (
                  <div key={type}>
                    <p className="text-xs font-medium text-muted-foreground mb-1 uppercase tracking-wider">
                      {type}
                    </p>
                    {templates.map(template => (
                      <Button
                        key={template.id}
                        variant={activeTemplate === template.id ? "default" : "ghost"}
                        className="w-full justify-start gap-2 mb-1"
                        onClick={() => setActiveTemplate(template.id)}
                      >
                        {template.icon}
                        <span className="truncate">{template.name}</span>
                      </Button>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            {/* Template Editor */}
            <div className="lg:col-span-2 space-y-4">
              {currentTemplate && (
                <>
                  <div className="flex items-center gap-2 mb-4">
                    <Badge variant="outline" className="flex items-center gap-1">
                      {currentTemplate.icon}
                      {currentTemplate.targetType}
                    </Badge>
                  </div>

                  {/* Variable Inputs */}
                  <div className="grid sm:grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
                    <h4 className="col-span-full text-sm font-medium flex items-center gap-2">
                      <Edit3 className="h-4 w-4" />
                      Personalization Variables
                    </h4>
                    {currentTemplate.variables.map(variable => (
                      <div key={variable}>
                        <Label htmlFor={variable} className="text-xs capitalize">
                          {variable.replace(/_/g, ' ')}
                        </Label>
                        <Input
                          id={variable}
                          placeholder={`Enter ${variable.replace(/_/g, ' ')}`}
                          value={variables[variable] || ''}
                          onChange={(e) => setVariables(prev => ({
                            ...prev,
                            [variable]: e.target.value
                          }))}
                          className="mt-1"
                        />
                      </div>
                    ))}
                  </div>

                  {/* Subject Line */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Subject Line</Label>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => copyToClipboard(currentTemplate.subject, 'subject')}
                        className="h-8"
                      >
                        {copiedId === 'subject' ? (
                          <Check className="h-4 w-4 text-green-500" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    <div className="p-3 bg-background border rounded-lg text-sm">
                      {replaceVariables(currentTemplate.subject)}
                    </div>
                  </div>

                  {/* Email Body */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Email Body</Label>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => copyToClipboard(currentTemplate.body, 'body')}
                        className="h-8"
                      >
                        {copiedId === 'body' ? (
                          <Check className="h-4 w-4 text-green-500" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    <div className="p-4 bg-background border rounded-lg text-sm whitespace-pre-wrap max-h-[400px] overflow-y-auto">
                      {replaceVariables(currentTemplate.body)}
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="flex gap-2 pt-4">
                    <Button 
                      onClick={() => copyToClipboard(currentTemplate.body, 'body')}
                      variant="outline"
                      className="flex-1"
                    >
                      <Copy className="h-4 w-4 mr-2" />
                      Copy Full Email
                    </Button>
                    <Button 
                      onClick={handleSendAndLog}
                      disabled={loading}
                      className="flex-1"
                    >
                      <Send className="h-4 w-4 mr-2" />
                      {loading ? "Logging..." : "Send & Log Outreach"}
                    </Button>
                  </div>
                  
                  {!selectedBacklink && (
                    <p className="text-xs text-muted-foreground text-center">
                      Select a backlink target above to enable automatic outreach logging
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default BacklinkEmailTemplates;
